import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  BoltIcon,
  Box,
  DebouncedTextField,
  DeleteConfirmButton,
  Flex,
  IconButton,
  MagnifyingGlassIcon,
  Spacer,
  Spinner,
  Table,
  TableProps,
  Text,
  toaster,
  Tooltip,
} from 'components'
import { TableAPI } from 'components/Table'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { SortDirection, SortingMode } from 'ka-table'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Lead, SessionWithLead, Sort } from 'types'
import {
  deleteLeadSessions,
  forceLight,
  getLead,
  sleep,
  updateLeads,
  useBreakpoint,
  usePermission,
  useTheme,
} from 'utils'
import { defaultProps } from 'utils/tableUtils'

import { EmptyState, FilterSelector, SessionExportButton } from '..'
import { useAggregations } from '../../utils/analyticsUtils'
import {
  flattenSession,
  LeadTableColumn,
  makeColumns,
  mobileDefSessionFields,
  useLeadSessions,
} from '../../utils/leadSessionUtils'
import { useOnTheGo } from '../../utils/onTheGoUtils'
import LeadTableColumnSelector from '../event/EventLeads/LeadTableColumnSelector'
import SessionDrawer from '../event/SessionDrawer'

const pageSize = 40

const initialTableProps: TableProps = {
  ...defaultProps,
  columnReordering: true,
  pinnedColumns: ['temp', 'name'],
}

interface Props {
  shrinkBanner: boolean
  updateShrinkBanner: (shrinkBanner: boolean) => void
}

export default function OnTheGoMemberLeads({ shrinkBanner, updateShrinkBanner }: Props) {
  const {
    filters,
    memberId,
    sessionCount,
    sessionCountLoading: baseSessionCountLoading,
  } = useOnTheGo()
  const { workspace } = useWorkspace()
  const permission = usePermission()
  const isMobile = useBreakpoint() < 2
  const { theme } = useTheme()
  const [tableProps, setTableProps] = useState(initialTableProps)
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [page, setPage] = useState(0)
  const [columns, setColumns] = useState<LeadTableColumn[]>([])
  const [defaultColumns, setDefaultColumns] = useState<LeadTableColumn[]>([])
  const [sort, setSort] = useState<Sort>({ by: 'created', order: 'DESC' })

  const { isFetching: filteredCountFetching, isLoading: filteredCountLoading } = useAggregations([
    {
      filters: filters?.length ? filters : undefined,
      table: 'leadSessions',
      values: ['_count'],
    },
  ])

  const listSessionCountLoading =
    baseSessionCountLoading || filteredCountLoading || filteredCountFetching

  const workspaceId = workspace?.id ?? ''
  const sessionFetchProps = {
    limit: pageSize,
    offset: page * pageSize,
    search,
    sort,
  }
  const memberIdSafe = memberId ?? ''
  const currentUser = useCurrentUser()
  const [sessions, sessionsStatus, hasAllSessions, updateSessions, removeSessions] =
    useLeadSessions(sessionFetchProps)
  const filteredSessionCount = sessions.length

  const sessionsRef = useRef(sessions)
  sessionsRef.current = sessions
  const tableAPI = useRef<TableAPI>(null)
  const allowUpdate = permission.workspace === 'admin' || memberId === currentUser?.id
  const ExpandIcon = shrinkBanner ? ArrowsPointingInIcon : ArrowsPointingOutIcon
  const selectedSession = sessions.find((s: SessionWithLead) => s.id === selectedId)
  const selectedSessions = useMemo(
    () => sessions.filter((s: SessionWithLead) => selectedIds.includes(s.id)),
    [selectedIds, sessions]
  )
  const canDeleteSelectedSessions = useMemo(
    () =>
      selectedSessions.length > 0 &&
      Boolean(currentUser?.id) &&
      selectedSessions.every((session: SessionWithLead) => session.createdBy === currentUser?.id),
    [currentUser?.id, selectedSessions]
  )

  const enableColumnReset = columns.some(c => {
    const defaultColumn = defaultColumns.find(d => d.key === c.key)
    return Boolean(defaultColumn?.visible) !== Boolean(c.visible)
  })

  const handleLeadUpdate = useCallback(
    async (updatedLead?: Partial<Lead> & { id: string }) => {
      setSelectedId('')
      if (!updatedLead) return

      function update(lead: Lead & { id: string }) {
        const impactedSessions = sessionsRef.current.filter(
          (s: SessionWithLead) => s.lead?.id === lead.id
        )
        updateSessions(
          impactedSessions.map((s: SessionWithLead) => ({
            ...s,
            lead: { ...s.lead!, ...lead },
          }))
        )
      }

      await updateLeads({ updates: [updatedLead] })
      update(updatedLead)

      await sleep(1000)

      const lead = (await getLead({ leadId: updatedLead.id })).data
      update(lead)
    },
    [updateSessions]
  )

  useEffect(() => {
    makeColumns({
      onColumnUpdate: allowUpdate ? handleLeadUpdate : undefined,
      workspaceId,
    }).then((cols: LeadTableColumn[]) => {
      setColumns(cols)
      setDefaultColumns(cols)
    })
  }, [handleLeadUpdate, allowUpdate, workspaceId])

  useEffect(() => {
    resetTable()
  }, [filters])

  const data = useMemo(() => {
    return sessions.map((s: SessionWithLead) => flattenSession(s))
  }, [sessions])

  const columnProps = useMemo(() => {
    const withSort = columns.map(c => {
      const sortDirection =
        c.key === sort.by
          ? sort.order === 'DESC'
            ? SortDirection.Descend
            : SortDirection.Ascend
          : undefined
      return { ...c, sortDirection }
    })

    if (!isMobile) return { columns: withSort }

    const mobileColumns = columns.map(c => ({
      ...c,
      visible: mobileDefSessionFields.includes(c.key),
      width: c.key === 'temp' ? 70 : c.width,
    }))
    return { columns: mobileColumns, pinnedColumns: ['name'] }
  }, [columns, sort, isMobile])

  const preventFetch = useRef(false)

  async function handleScrollToBottom() {
    if (preventFetch.current || hasAllSessions) return
    setPage(s => s + 1)
    preventFetch.current = true
    await sleep(500)
    preventFetch.current = false
  }

  const handleTableChange: TableProps['onChange'] = (props, action) => {
    switch (action.type) {
      case 'UpdateSortDirection': {
        const { columnKey } = action
        let direction: Sort['order'] = 'ASC'
        if (columnKey === sort.by) direction = sort.order === 'ASC' ? 'DESC' : 'ASC'
        else if (columnKey === 'created') direction = 'DESC'
        if (columnKey === 'name') setSort({ by: 'firstName', order: direction })
        else setSort({ by: columnKey, order: direction })
        resetTable()
        break
      }
      case 'ReorderColumns': {
        const { columnKey, targetColumnKey } = action
        const index = columns.findIndex(c => c.key === columnKey)
        const targetIndex = columns.findIndex(c => c.key === targetColumnKey)
        const newColumns = [...columns]
        newColumns.splice(index, 1)
        newColumns.splice(targetIndex, 0, columns[index])
        setColumns(newColumns)
        break
      }
      case 'ResizeColumn': {
        const { columnKey, width } = action
        const newColumns = columns.map(c => (c.key === columnKey ? { ...c, width } : c))
        setColumns(newColumns)
        break
      }
      default: {
        setTableProps(props)
      }
    }
  }

  function updateSelectedRows(newSelectedRows: string[]) {
    setSelectedIds(newSelectedRows)
  }

  async function handleDelete() {
    if (!workspaceId || !canDeleteSelectedSessions) return

    removeSessions(selectedIds)
    updateSelectedRows([])

    try {
      await deleteLeadSessions({
        deleteOrphanedLeads: true,
        eventId: '',
        sessionIds: selectedIds,
        workspaceId,
      })
    } catch (e) {
      console.error('Error deleting sessions:', e)
      toaster.create({
        description: 'Something went wrong. Refresh the page and try again.',
        title: 'Error deleting sessions',
        type: 'error',
      })
    }
  }

  function resetTable() {
    setPage(0)
    tableAPI.current?.scrollToTop()
  }

  if (!memberIdSafe || !workspaceId) return null

  return (
    <>
      <Flex
        direction="column"
        flex={1}
        gap={3}
        h="full"
        minH={0}
        mx={{ base: 1, md: 0 }}
        overflow="hidden"
      >
        <Flex flexShrink={0} gap={2} p={1}>
          <DebouncedTextField
            backdropFilter="blur(14px)"
            bg={theme === 'light' ? 'rgba(255, 255, 255, 0.74)' : 'rgba(255, 255, 255, 0.14)'}
            borderRadius="10px"
            borderWidth={0}
            boxShadow="0px 1px 4px 0px rgba(0, 0, 0, 0.06)"
            controlled
            leftElement={<MagnifyingGlassIcon />}
            onChange={v => {
              setSearch(v)
              resetTable()
            }}
            pl={10}
            placeholder="Search..."
            value={search}
            width={{ base: '100%', sm: '250px' }}
          />
          <Flex flex={1} justifyContent="space-between" gap={2} align="center">
            <Box />
            <Flex gap={2} hideBelow="md">
              <FilterSelector columns={columns} />

              <LeadTableColumnSelector
                columns={columns}
                onChange={setColumns}
                onReset={enableColumnReset ? () => setColumns(defaultColumns) : undefined}
              />
              <SessionExportButton />
            </Flex>
          </Flex>
        </Flex>

        {listSessionCountLoading ? (
          <Flex align="center" flex={1} justify="center" minH={0}>
            <Spinner />
          </Flex>
        ) : sessionCount > 0 ? (
          <Box
            borderRadius="14px"
            display="flex"
            flex={1}
            flexDir="column"
            minH={0}
            overflowX="hidden"
            overflowY="auto"
          >
            <Box
              {...forceLight}
              alignItems="center"
              backgroundColor="white"
              borderBottomWidth="1px solid rgba(203, 203, 203, 0.4)"
              borderTopRadius="14px"
              display="flex"
              flexShrink={0}
              gap={2}
              h="47px"
              justifyContent="center"
              overflow="hidden"
              pl={4}
            >
              <BoltIcon color="primary.500" fontSize="md" />
              {selectedIds.length > 0 ? (
                <Flex align="center" gap={2}>
                  <Text
                    borderRightWidth={'2px solid rgba(23, 23, 23, 0.2)'}
                    pr={3}
                    textStyle={'bodySmall'}
                  >
                    {selectedIds.length} of {sessionCount.toLocaleString()} Sessions Selected
                  </Text>
                  <DeleteConfirmButton
                    description={`Type "Delete" if you are certain you want to delete the selected ${
                      selectedIds.length > 1 ? 'sessions' : 'session'
                    }`}
                    disabled={!canDeleteSelectedSessions}
                    onDelete={handleDelete}
                    textStyle="bodySmall"
                  />
                </Flex>
              ) : filters?.length ? (
                <Text textStyle="bodySmall">
                  Filtered Sessions: {filteredSessionCount.toLocaleString()} of{' '}
                  {sessionCount.toLocaleString()}
                </Text>
              ) : (
                <Text textStyle="bodySmall">
                  Total Sessions: {sessionCount.toLocaleString() || '--'}
                </Text>
              )}
              <Spacer />
              <Tooltip
                content={`${shrinkBanner ? 'Collapse' : 'Expand'} table`}
                positioning={{ placement: 'top' }}
              >
                <IconButton
                  aria-label="expand table"
                  icon={<ExpandIcon size="sm" />}
                  variant="ghost"
                  onClick={() => updateShrinkBanner(!shrinkBanner)}
                />
              </Tooltip>
            </Box>

            <Table
              ref={tableAPI}
              {...tableProps}
              {...columnProps}
              data={data}
              emptyText="No sessions"
              loading={{ enabled: sessionsStatus === 'loading' }}
              searchText={search}
              sortingMode={hasAllSessions ? SortingMode.Single : SortingMode.SingleRemote}
              onChange={handleTableChange}
              onRowClick={setSelectedId}
              onScrollToBottom={handleScrollToBottom}
              onSelectedRowsChange={isMobile ? undefined : updateSelectedRows}
              selectedRows={isMobile ? undefined : selectedIds}
            />
          </Box>
        ) : filters?.length ? (
          <Flex align="center" flex={1} justify="center" minH={0}>
            {sessionsStatus === 'loading' ? (
              <Spinner />
            ) : (
              <EmptyState title="No data retrieved, change filters and try again" />
            )}
          </Flex>
        ) : (
          <Flex align="center" flex={1} justify="center" minH={0} p={10}>
            <Text opacity={0.7} textStyle="bodyMedium">
              No On-the-Go sessions for this member yet.
            </Text>
          </Flex>
        )}
      </Flex>

      <SessionDrawer session={selectedSession} onDone={handleLeadUpdate} />
    </>
  )
}
